import { selectors } from "../../../../add-element/cypressSelectors";
import {
  ATTENDING,
  ATTENDING_STATUS_LABEL,
  AWAITING_REPLY,
  MAYBE,
  NOT_ATTENDING,
} from "../../../../guests/constants";
import { isEmailValid } from "../../../../shared/validations/emailValidations";
import { QUESTION_WITH_ERROR_CLASSNAME } from "../constants";
import type {
  ContactData,
  Guest,
  GuestResponse,
  Question as QuestionModel,
  RSVPEvent,
} from "../types";
import { css } from "@emotion/react";
import {
  theme,
  Divider,
  ButtonPicker,
  ButtonPickerButton,
  NumericStepper,
  Textarea,
  FadeReveal,
  styleUtils,
  Icon,
  TextInput,
} from "@minted/minted-components";
import classNames from "classnames";
import isEmpty from "lodash/isEmpty";
import React, { useCallback, type ReactNode } from "react";

const eventGuestStyles = {
  attendingStatusText: css`
    ${theme.typeStyles.bodyMediumStyles}
  `,
  attendingStatusWrapper: css`
    display: flex;
    justify-content: end;
    align-items: center;
    width: fit-content;
    float: right;
  `,
  buttonPickerWrapper: (hasError?: boolean) => css`
    ${hasError &&
    `
      button {
        border: 1px solid ${theme.colors.alert};
        &:hover {
          border: 1px solid ${theme.colors.alert};
        }
      }
    `}

    button {
      text-transform: none;
    }
  `,
  card: css`
    background-color: ${theme.colors.gray100};
    border: ${styleUtils.rem(1)} solid ${theme.colors.gray300};
    border-radius: ${theme.spacing.base};
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.x6};
    padding: ${theme.spacing.x4};
    padding-bottom: ${theme.spacing.x4};
  `,
  column: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.x2};
  `,
  dividerWrapper: css`
    margin-top: ${theme.spacing.base};
    margin-bottom: ${theme.spacing.base};
  `,
  emailDisclaimer: css`
    ${theme.typeStyles.bodySmallStyles};
    color: ${theme.colors.textSecondary};
  `,
  errorStyle: css`
    ${theme.typeStyles.bodySmallStyles};
    color: #cb2647;
    margin-top: 0.25rem;
  `,
  fadeReveal: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.x6};
  `,
  fullWidth: css`
    width: 100%;
  `,
  headerWrapper: css`
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
  `,
  nameText: css`
    ${theme.typeStyles.titleMediumStyles};
    float: left;
  `,
  numericStepperText: css`
    ${theme.typeStyles.bodySmallStyles};
    ${theme.typeStyles.bodyBoldStyles};
  `,
  totalAttendingWrapper: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.x4};
  `,
  attendingRow: css`
    align-items: center;
    display: flex;
    justify-content: space-between;
  `,
  openInviteInputWrapper: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.x2};
  `,
  questionText: css`
    ${theme.typeStyles.titleAlternateMediumStyles};
  `,
  questionWrapper: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.x4};
  `,
  readOnlyNoteText: css`
    ${theme.typeStyles.bodySmallStyles};
    text-align: center;
  `,
  readOnlyResponseText: css`
    ${theme.typeStyles.bodySmallStyles}
  `,
  row: css`
    display: flex;
    gap: ${theme.spacing.x2};
  `,
  // This exists because the error spacing will actually render outside of the textarea allocated space.
  // Something to do with the textarea inheriting display: block; somewhere, and seems to be an issue
  // that happens only for hitchedup on Chrome. Could be a global styling somewhere.
  textAreaOverrides: css`
    & > * {
      display: flex;
      flex-direction: column;
    }
  `,
};

type ButtonPickerWrapperProps = {
  children: ReactNode;
  hasError?: boolean;
};

const ButtonPickerWrapper = ({
  children,
  hasError,
}: ButtonPickerWrapperProps) => (
  <div css={eventGuestStyles.buttonPickerWrapper(hasError)}>
    {children}
    {hasError && (
      <div css={eventGuestStyles.errorStyle}>Question is required</div>
    )}
  </div>
);

type QuestionProps = {
  answer?: string;
  onChange: (event: any, questionId: number) => void;
  question: QuestionModel;
  readOnly?: boolean;
  shouldValidateErrors?: boolean;
};

export const Question = ({
  answer,
  onChange,
  question,
  readOnly,
  shouldValidateErrors,
}: QuestionProps) => {
  const hasError = question.required && isEmpty(answer) && shouldValidateErrors;
  const errorMessage = "Question is required";

  return (
    <div
      className={classNames({
        [QUESTION_WITH_ERROR_CLASSNAME]: hasError,
      })}
      css={eventGuestStyles.questionWrapper}
    >
      <div css={eventGuestStyles.questionText}>
        {question.text}
        {!question.required && " (Optional)"}
      </div>

      {readOnly ? (
        <div css={eventGuestStyles.readOnlyResponseText}>
          {answer || "(skipped)"}
        </div>
      ) : (
        <>
          {question.questionType === "freetext" && (
            <div css={eventGuestStyles.textAreaOverrides}>
              <Textarea
                error={hasError ? errorMessage : undefined}
                hasErrorSpacing
                minRows={2}
                name={question.text}
                onChange={(event) => onChange(event, question.id)}
                placeholder="Your message"
                touched
                value={answer}
              />
            </div>
          )}

          {question.questionType === "multiple_choice" && (
            <ButtonPickerWrapper hasError={hasError}>
              <ButtonPicker
                name={question.text}
                onChange={(event) => onChange(event, question.id)}
                size="small"
                value={answer}
              >
                {(question.choices ?? []).map((choice) => (
                  <ButtonPickerButton key={choice} value={choice}>
                    {choice}
                  </ButtonPickerButton>
                ))}
              </ButtonPicker>
            </ButtonPickerWrapper>
          )}
        </>
      )}
    </div>
  );
};

// adultCount/childCount/increment-decrement/updateContactData/updateGuestResponse/
// updateNoteToHost are only read in the editable (!readOnly) branch, so the
// read-only summary caller doesn't pass them.
type Props = {
  adultCount?: number;
  childCount?: number;
  contactData: ContactData;
  decrementAdultCount?: () => void;
  decrementChildCount?: () => void;
  event?: RSVPEvent;
  guest: Guest;
  guestResponse: GuestResponse;
  incrementAdultCount?: () => void;
  incrementChildCount?: () => void;
  maxPartySize?: number;
  noteToHost?: string;
  partySize?: number;
  questions?: QuestionModel[];
  readOnly?: boolean;
  separateAdultChild?: boolean;
  showNoteToHost?: boolean;
  updateContactData?: (fieldName: string, newValue: string) => void;
  updateGuestResponse?: (newResponse: GuestResponse) => void;
  updateNoteToHost?: (value: string) => void;
  validateFields?: boolean;
};

const isAttendingOrMaybe = (responseType: string) =>
  responseType === ATTENDING || responseType === MAYBE;

const getReadOnlyRsvpStatusIconType = (
  responseType: string,
): "check" | "help" | "close" => {
  switch (responseType) {
    case ATTENDING:
      return "check";
    case MAYBE:
      return "help";
    default:
      return "close";
  }
};

const getReadOnlyRsvpStatusIconFillStyles = (responseType: string) => {
  if (responseType === MAYBE) {
    return undefined;
  }

  const fill =
    responseType === NOT_ATTENDING
      ? theme.colors.negative600
      : theme.colors.accent600;

  return css`
    svg > path {
      fill: ${fill};
    }
  `;
};

const OnlineInvitationEventGuest = ({
  adultCount = 0,
  childCount = 0,
  contactData,
  decrementAdultCount = () => {},
  decrementChildCount = () => {},
  event = {} as RSVPEvent,
  guest,
  guestResponse,
  incrementAdultCount = () => {},
  incrementChildCount = () => {},
  maxPartySize,
  noteToHost,
  partySize,
  questions,
  readOnly,
  separateAdultChild = false,
  showNoteToHost,
  updateContactData = () => {},
  updateGuestResponse = () => {},
  updateNoteToHost = () => {},
  validateFields,
}: Props) => {
  const showQuestions =
    (!readOnly && !isEmpty(questions)) ||
    (readOnly &&
      isAttendingOrMaybe(guestResponse.responseType) &&
      !isEmpty(questions));

  const onAttendingChange = useCallback(
    (event: any) => {
      event.persist();

      updateGuestResponse({
        ...guestResponse,
        responseType: event.target.value,
      });
    },
    [updateGuestResponse, guestResponse],
  );

  const onAnswerChange = useCallback(
    (event: any, questionId: number) => {
      event.persist();

      updateGuestResponse({
        ...guestResponse,
        answers: {
          ...guestResponse.answers,
          [questionId]: event.target.value,
        },
      });
    },
    [guestResponse, updateGuestResponse],
  );

  const iconFillColor = getReadOnlyRsvpStatusIconFillStyles(
    guestResponse.responseType,
  );

  const hasStatusError =
    guestResponse.responseType === AWAITING_REPLY && validateFields;

  let nameErrorMessage: string | undefined;
  let emailErrorMessage: string | undefined;

  if (validateFields) {
    if (!contactData.name) {
      nameErrorMessage = "Name is required";
    }

    if (isAttendingOrMaybe(guestResponse.responseType) && !contactData.email) {
      emailErrorMessage = "Email is required";
    } else if (contactData.email && !isEmailValid(contactData.email)) {
      emailErrorMessage = "Email is not valid";
    }
  }

  return (
    <div css={eventGuestStyles.card}>
      <>
        <div css={eventGuestStyles.headerWrapper}>
          <div css={eventGuestStyles.nameText}>
            {contactData.id ? contactData.name : "RSVP"}
          </div>

          {readOnly && (
            <div
              css={css`
                ${eventGuestStyles.attendingStatusWrapper};
                ${iconFillColor}
              `}
            >
              <Icon
                size="small"
                type={getReadOnlyRsvpStatusIconType(guestResponse.responseType)}
              />

              <div css={eventGuestStyles.attendingStatusText}>
                {` ${partySize} `}
                {ATTENDING_STATUS_LABEL[guestResponse.responseType]}
              </div>
            </div>
          )}
        </div>

        {showQuestions && (
          <div css={eventGuestStyles.dividerWrapper}>
            <Divider spacing="none" type="light" />
          </div>
        )}

        {readOnly && noteToHost && (
          <>
            <div css={eventGuestStyles.dividerWrapper}>
              <Divider spacing="none" type="light" />
            </div>
            <div css={eventGuestStyles.readOnlyNoteText}>
              &ldquo;{noteToHost}&rdquo;
            </div>
          </>
        )}
      </>

      {!readOnly && (
        <>
          <div
            className={classNames({
              [QUESTION_WITH_ERROR_CLASSNAME]: hasStatusError,
            })}
            css={eventGuestStyles.questionWrapper}
            data-cy={selectors.rsvp_attendingPicker}
          >
            <div css={eventGuestStyles.questionText}>
              Will you be attending this event?
            </div>

            <ButtonPickerWrapper hasError={hasStatusError}>
              <ButtonPicker
                layout="horizontalLayout"
                name="attending"
                onChange={onAttendingChange}
                size="small"
                value={guestResponse.responseType}
              >
                <ButtonPickerButton value={ATTENDING}>Yes</ButtonPickerButton>

                {event.allowMaybeRsvp && (
                  <ButtonPickerButton value={MAYBE}>Maybe</ButtonPickerButton>
                )}

                <ButtonPickerButton value={NOT_ATTENDING}>
                  No
                </ButtonPickerButton>
              </ButtonPicker>
            </ButtonPickerWrapper>
          </div>
          {
            // If open invite response and a response is selected, display
            guestResponse.responseType !== AWAITING_REPLY &&
              !contactData.id && (
                <div css={eventGuestStyles.openInviteInputWrapper}>
                  <div css={eventGuestStyles.numericStepperText}>
                    {isAttendingOrMaybe(guestResponse.responseType)
                      ? "Your Details"
                      : "Confirm your name"}
                  </div>
                  <div
                    className={classNames({
                      [QUESTION_WITH_ERROR_CLASSNAME]:
                        Boolean(nameErrorMessage),
                    })}
                  >
                    <TextInput
                      error={nameErrorMessage}
                      label="Your Name"
                      name="newContactName"
                      onChange={(event) =>
                        updateContactData("name", event.target.value)
                      }
                      touched
                      value={contactData.name}
                    />
                  </div>
                  <div
                    className={classNames({
                      [QUESTION_WITH_ERROR_CLASSNAME]:
                        Boolean(emailErrorMessage),
                    })}
                  >
                    <TextInput
                      error={emailErrorMessage}
                      label={`Your Email${!isAttendingOrMaybe(guestResponse.responseType) ? " (Optional)" : ""}`}
                      name="newContactEmail"
                      onChange={(event) =>
                        updateContactData("email", event.target.value)
                      }
                      touched
                      value={contactData.email}
                    />
                  </div>
                  {isAttendingOrMaybe(guestResponse.responseType) && (
                    <div css={eventGuestStyles.emailDisclaimer}>
                      We&apos;ll only send you important updates about this
                      event, nothing else
                    </div>
                  )}
                </div>
              )
          }

          {showNoteToHost && (
            <div
              css={eventGuestStyles.textAreaOverrides}
              data-cy={selectors.oi_rsvp_noteToHost}
            >
              <Textarea
                label="Private note for host (optional)"
                maxLength={500}
                minRows={5}
                name="note-to-host"
                onChange={(event) => updateNoteToHost(event.target.value)}
                resizable={false}
                value={noteToHost}
              />
            </div>
          )}

          {isAttendingOrMaybe(guestResponse.responseType) &&
            (separateAdultChild ? (
              <div css={eventGuestStyles.totalAttendingWrapper}>
                <div css={eventGuestStyles.questionText}>Total Attending</div>
                <div css={eventGuestStyles.attendingRow}>
                  <div css={eventGuestStyles.numericStepperText}>Adults</div>
                  <div data-cy={selectors.oi_rsvp_adultStepper}>
                    <NumericStepper
                      maxValue={(maxPartySize ?? 1) - childCount}
                      minValue={0}
                      onMinusClick={decrementAdultCount}
                      onPlusClick={incrementAdultCount}
                      size="small"
                      value={adultCount}
                    />
                  </div>
                </div>
                <div css={eventGuestStyles.attendingRow}>
                  <div css={eventGuestStyles.numericStepperText}>Children</div>
                  <div data-cy={selectors.oi_rsvp_childStepper}>
                    <NumericStepper
                      maxValue={(maxPartySize ?? 1) - adultCount}
                      minValue={0}
                      onMinusClick={decrementChildCount}
                      onPlusClick={incrementChildCount}
                      size="small"
                      value={childCount}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div css={eventGuestStyles.questionWrapper}>
                <div css={eventGuestStyles.numericStepperText}>
                  Number Attending
                </div>
                <div data-cy={selectors.oi_rsvp_partySizeStepper}>
                  <NumericStepper
                    maxValue={maxPartySize}
                    minValue={1}
                    onMinusClick={decrementAdultCount}
                    onPlusClick={incrementAdultCount}
                    size="small"
                    value={adultCount}
                  />
                </div>
              </div>
            ))}
        </>
      )}

      {showQuestions && (
        <FadeReveal
          css={eventGuestStyles.fadeReveal}
          in={
            isAttendingOrMaybe(guestResponse.responseType) &&
            !isEmpty(questions)
          }
          unmountOnExit
        >
          {(questions ?? []).map((question) => (
            <Question
              answer={guestResponse.answers[question.id]}
              key={`${guest.id}${question.id}`}
              onChange={onAnswerChange}
              question={question}
              readOnly={readOnly}
              shouldValidateErrors={
                isAttendingOrMaybe(guestResponse.responseType) && validateFields
              }
            />
          ))}
        </FadeReveal>
      )}
    </div>
  );
};

export default OnlineInvitationEventGuest;
